import React from "react";
import "./Button.scss";

export type ButtonVariant = "primary" | "brand" | "tint" | "ghost" | "danger" | "danger-tint" | "link";
export type ButtonSize = "sm" | "md" | "lg";

type ButtonStyleOptions = {
  /**
   * primary = orange (the main action) · brand = blue · tint = orange tint (a second action) · ghost = quiet grey
   * (Cancel, Back) · danger = red (Delete) · danger-tint = red text on a red tint · link = text only
   */
  variant?: ButtonVariant;
  /** sm 32px, md 40px, lg 46px */
  size?: ButtonSize;
  /** Full width */
  block?: boolean;
};

/** The classes of a kit button, for a router <Link> or <a> that should look like one. */
export const buttonClassName = ({ variant = "primary", size = "md", block = false }: ButtonStyleOptions = {}, extra?: string) =>
  ["ui-btn", `ui-btn--${variant}`, `ui-btn--${size}`, block ? "ui-btn--block" : "", extra].filter(Boolean).join(" ");

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  ButtonStyleOptions & {
    /** Busy: disabled, aria-busy and a spinner in place of the icon */
    loading?: boolean;
    /** An icon before the label (a lucide / mynaui element) */
    icon?: React.ReactNode;
    /** An icon after the label */
    iconRight?: React.ReactNode;
  };

/** The slanted brand button. `type` defaults to "button"; pass type="submit" in forms. */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", block = false, loading = false, icon, iconRight, className, disabled, type = "button", children, ...rest },
  ref
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={buttonClassName({ variant, size, block }, [loading ? "ui-btn--loading" : "", className].filter(Boolean).join(" "))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="ui-btn__spinner" aria-hidden="true" /> : icon ? <span className="ui-btn__icon">{icon}</span> : null}
      {children}
      {iconRight && !loading ? <span className="ui-btn__icon">{iconRight}</span> : null}
    </button>
  );
});

export default Button;
